/**
 * Resumable chunked uploads (protocol in backend/app/services/uploads.py).
 *
 * - Each chunk is hashed with SHA-256 (WebCrypto) and the server verifies it, so damaged
 *   chunks are re-sent. The server computes the whole-file checksum after the last chunk.
 * - Network failures retry with backoff, resuming from the server's offset.
 * - The upload id is remembered per file (name + size + modified time) so a page reload
 *   or browser restart can resume instead of starting over.
 */
import { ApiError, api, ensureCsrf } from "./api";

export type UploadState =
  "queued" | "uploading" | "finishing" | "checking" | "ready" | "rejected" | "error" | "cancelled";

export interface UploadInfo {
  id: string;
  filename: string;
  size: number;
  bytes_received: number;
  state: "uploading" | "checking" | "ready" | "attached" | "rejected" | "cancelled" | "expired";
  message: string | null;
  chunk_max_bytes: number;
}

export interface UploadProgress {
  state: UploadState;
  uploadId?: string;
  sent: number;
  total: number;
  message?: string;
}

const RESUME_KEY = "bb-upload-resume";
const MAX_RETRIES = 6;

function fileKey(file: File) {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

function readResume(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(RESUME_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function writeResume(map: Record<string, string>) {
  try {
    localStorage.setItem(RESUME_KEY, JSON.stringify(map));
  } catch {
    /* storage unavailable - resume after reload will not be possible */
  }
}

function remember(file: File, id: string | null) {
  const map = readResume();
  if (id) map[fileKey(file)] = id;
  else delete map[fileKey(file)];
  writeResume(map);
}

async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    });
  });

/** PUT one chunk with XHR so we get byte-level progress. Resolves with server info. */
function putChunk(
  id: string,
  offset: number,
  body: ArrayBuffer,
  sha: string,
  csrf: string,
  onProgress: (loaded: number) => void,
  signal: AbortSignal,
): Promise<UploadInfo> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", `/api/v1/uploads/${id}/chunk`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("Upload-Offset", String(offset));
    xhr.setRequestHeader("Upload-Chunk-SHA256", sha);
    xhr.setRequestHeader("X-CSRF-Token", csrf);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      let data: unknown = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        /* ignore */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data as UploadInfo);
      else {
        const err = (
          data as {
            error?: {
              code: string;
              message: string;
              fields?: { field: string; message: string }[];
            };
          }
        )?.error;
        reject(
          new ApiError(
            xhr.status,
            err?.code ?? "error",
            err?.message ?? "Upload failed.",
            err?.fields ?? [],
          ),
        );
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "network_error", "Connection problem."));
    signal.addEventListener("abort", () => xhr.abort());
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    xhr.send(body);
  });
}

/** Where to start an upload. Customers use /uploads; staff deliveries use their own endpoint. */
export type InitiateFn = (file: File) => Promise<UploadInfo>;

const customerInitiate: InitiateFn = (file) =>
  api.post<UploadInfo>("/uploads", { filename: file.name, size: file.size, scope: "order" });

export async function uploadFile(
  file: File,
  onUpdate: (p: UploadProgress) => void,
  signal: AbortSignal,
  defaultChunk = 8 * 1024 * 1024,
  initiate: InitiateFn = customerInitiate,
): Promise<UploadInfo> {
  const total = file.size;
  let info: UploadInfo | null = null;

  // Resume a previous upload of the same file if the server still has it.
  const previous = readResume()[fileKey(file)];
  if (previous) {
    try {
      const existing = await api.get<UploadInfo>(`/uploads/${previous}`);
      if (["uploading", "checking", "ready"].includes(existing.state)) info = existing;
    } catch {
      /* gone - start fresh */
    }
  }
  if (!info) {
    info = await initiate(file);
    remember(file, info.id);
  }
  const id = info.id;
  const chunkSize = Math.min(defaultChunk, info.chunk_max_bytes || defaultChunk);
  let offset = info.bytes_received;
  let retries = 0;

  while (info.state === "uploading" && offset < total) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    onUpdate({ state: "uploading", uploadId: id, sent: offset, total });
    const buf = await file.slice(offset, Math.min(offset + chunkSize, total)).arrayBuffer();
    const sha = await sha256Hex(buf);
    try {
      const csrf = await ensureCsrf();
      const base = offset;
      info = await putChunk(
        id,
        offset,
        buf,
        sha,
        csrf,
        (loaded) => onUpdate({ state: "uploading", uploadId: id, sent: base + loaded, total }),
        signal,
      );
      offset = info.bytes_received;
      retries = 0;
    } catch (err) {
      if ((err as Error).name === "AbortError") throw err;
      if (err instanceof ApiError && err.code === "offset_mismatch") {
        offset = Number(err.fields[0]?.message ?? offset);
        continue;
      }
      if (err instanceof ApiError && err.code === "csrf_failed") {
        await ensureCsrf(true);
        continue;
      }
      const retryable =
        err instanceof ApiError &&
        (err.status === 0 || err.status >= 500 || err.code === "chunk_checksum_mismatch");
      if (!retryable || retries >= MAX_RETRIES) throw err;
      retries++;
      await sleep(Math.min(1000 * 2 ** retries, 30_000), signal);
      // Ask the server where we really are before retrying.
      const status = await api.get<UploadInfo>(`/uploads/${id}`).catch(() => null);
      if (status) offset = status.bytes_received;
    }
  }

  if (info.state === "uploading") {
    onUpdate({ state: "finishing", uploadId: id, sent: total, total });
    info = await api.post<UploadInfo>(`/uploads/${id}/finish`);
  }
  // Wait for the security scan.
  while (info.state === "checking") {
    onUpdate({
      state: "checking",
      uploadId: id,
      sent: total,
      total,
      message: info.message ?? undefined,
    });
    await sleep(2000, signal);
    info = await api.get<UploadInfo>(`/uploads/${id}`);
  }
  remember(file, null);
  if (info.state === "ready" || info.state === "attached") {
    onUpdate({ state: "ready", uploadId: id, sent: total, total });
  } else {
    onUpdate({
      state: "rejected",
      uploadId: id,
      sent: total,
      total,
      message: info.message ?? "This file was not accepted.",
    });
  }
  return info;
}

export async function cancelUpload(id: string) {
  await api.del(`/uploads/${id}`).catch(() => undefined);
}

export function forgetResume(file: File) {
  remember(file, null);
}
